import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { optionFor, type Club, type Duration, type Slot } from '../../shared/types'
import { fromMinutes, toMinutes } from '../../shared/time'
import { formatPrice, shortDate, weekdayShort } from '../lib/format'
import { useI18n } from '../lib/i18n'
import { useScrollLock } from '../lib/useScrollLock'
import type { CellRef } from './WeekGrid'

interface ContentProps {
  cell: CellRef
  clubs: Map<string, Club>
  duration: Duration
}

const PLATFORM_LABEL: Record<string, string> = {
  simplifica: 'Simplifica',
  playtomic: 'Playtomic',
  tieplayer: 'TieSports',
  field: 'Field',
  matchpoint: 'MatchPoint',
}

/**
 * Everything free in one cell, grouped by club *and* exact time - a cell covers
 * a half-hour band, so two clubs in it can genuinely start at different minutes.
 * Cheapest first, because that is what people are actually comparing.
 */
function SlotList({ cell, clubs, duration, limit }: ContentProps & { limit?: number }) {
  const { t, locale } = useI18n()

  /**
   * Courts at the same club and time can carry different prices - Porto Santo
   * charges up to EUR 21 more for one court than another in the same slot - and
   * the API returns them in no particular order. Quote the cheapest bookable
   * one, since that is the price the visitor can actually get.
   */
  const cheapest = (slots: Slot[]): number | null => {
    const prices = slots
      .map((s) => optionFor(s, duration)?.price)
      .filter((p): p is number => p !== null && p !== undefined)
    return prices.length > 0 ? Math.min(...prices) : null
  }

  const grouped = new Map<string, Slot[]>()
  for (const slot of cell.slots) {
    const key = `${slot.clubId}|${slot.start}`
    const bucket = grouped.get(key)
    if (bucket) bucket.push(slot)
    else grouped.set(key, [slot])
  }

  const all = [...grouped.values()].sort((a, b) => {
    const pa = cheapest(a) ?? Number.POSITIVE_INFINITY
    const pb = cheapest(b) ?? Number.POSITIVE_INFINITY
    return (
      pa - pb ||
      a[0].start.localeCompare(b[0].start) ||
      (clubs.get(a[0].clubId)?.name ?? '').localeCompare(clubs.get(b[0].clubId)?.name ?? '')
    )
  })

  // On a busy afternoon a dozen clubs are free at once, which would run the
  // hover card off the screen. Cheapest-first means the top few are the ones
  // worth showing anyway.
  const groups = limit ? all.slice(0, limit) : all
  const hidden = all.length - groups.length

  return (
    <div className="flex flex-col">
      <header
        className="sticky top-0 z-10 flex items-baseline justify-between gap-3 border-b px-3.5 py-2.5"
        style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
      >
        <span className="nums text-sm font-semibold">{t.from} {cell.time}</span>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {weekdayShort(cell.date, locale)} {shortDate(cell.date, locale)}
        </span>
      </header>

      <ul className="flex flex-col">
        {groups.map((slots) => {
          const first = slots[0]
          const club = clubs.get(first.clubId)
          const courts = [...new Set(slots.map((s) => s.courtName).filter(Boolean))]
          const price = cheapest(slots)
          const end = fromMinutes(toMinutes(first.start) + duration)
          return (
            <li
              key={`${first.clubId}|${first.start}`}
              className="border-b px-3.5 py-2.5 last:border-b-0"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="size-2 shrink-0 rounded-full"
                      style={{ background: club?.color ?? 'var(--color-brand-400)' }}
                    />
                    <span className="truncate text-sm font-medium">
                      {club?.name ?? first.clubId}
                    </span>
                  </div>
                  <div className="nums mt-0.5 pl-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                    {first.start}-{end}
                    {club?.municipality ? ` · ${club.municipality}` : ''}
                  </div>
                  <div className="mt-0.5 pl-4 text-xs" style={{ color: 'var(--text-faint)' }}>
                    {courts.length > 0 ? courts.join(' · ') : t.courtAtClub}
                  </div>
                </div>
                <span
                  className="nums shrink-0 text-sm font-semibold"
                  style={{ color: price === 0 ? 'var(--color-brand-400)' : undefined }}
                >
                  {price === 0 ? t.free : (formatPrice(price, first.currency, locale) ?? t.priceOnSite)}
                </span>
              </div>

              <a
                href={first.bookingUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-2 ml-4 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors"
                style={{ background: 'var(--surface-2)', color: 'var(--text)' }}
              >
                {club?.source === 'manual'
                  ? t.contactClub
                  : t.bookOn(PLATFORM_LABEL[club?.source ?? ''] ?? '')}
                <span aria-hidden>↗</span>
              </a>
            </li>
          )
        })}
      </ul>

      {hidden > 0 && (
        <p
          className="border-t px-3.5 py-2 text-xs"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
        >
          {t.moreClubs(hidden)}
        </p>
      )}
    </div>
  )
}

const CARD_W = 300
const MARGIN = 10

/** Desktop: a card pinned beside the hovered cell, flipped to stay on screen. */
export function HoverCard({ cell, clubs, duration }: ContentProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: -9999, top: -9999 })

  useLayoutEffect(() => {
    const h = ref.current?.offsetHeight ?? 200
    const { rect } = cell

    let left = rect.right + MARGIN
    if (left + CARD_W > window.innerWidth - MARGIN) left = rect.left - CARD_W - MARGIN
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - CARD_W - MARGIN))

    const top = Math.max(
      MARGIN,
      Math.min(rect.top + rect.height / 2 - h / 2, window.innerHeight - h - MARGIN),
    )

    setPos({ left, top })
  }, [cell])

  return (
    <div
      ref={ref}
      role="tooltip"
      className="card pointer-events-none fixed z-50 overflow-hidden"
      style={{ left: pos.left, top: pos.top, width: CARD_W }}
    >
      <SlotList cell={cell} clubs={clubs} duration={duration} limit={6} />
    </div>
  )
}

/**
 * Opened by a click on any device. On touch it is the only way in, since there
 * is no hover; on desktop it is the way to see past the hover card's cap and to
 * actually reach the booking links.
 */
export function SlotSheet({
  cell,
  clubs,
  duration,
  onClose,
}: ContentProps & { onClose: () => void }) {
  const { t } = useI18n()
  useScrollLock(true)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
      <button
        type="button"
        aria-label={t.close}
        className="absolute inset-0"
        style={{ background: 'rgb(0 0 0 / 0.5)' }}
        onClick={onClose}
      />
      {/* A column, not one big scroller: the grab handle has to stay put while
          the list moves under it, and the list needs min-h-0 or flex refuses to
          let it shrink enough to scroll at all. */}
      <div
        role="dialog"
        aria-modal="true"
        className="card relative flex max-h-[85dvh] w-full flex-col rounded-b-none sm:max-w-sm sm:rounded-[var(--radius-card)]"
      >
        <div className="flex shrink-0 justify-center pt-2 pb-1 sm:hidden">
          <span className="h-1 w-9 rounded-full" style={{ background: 'var(--border-strong)' }} />
        </div>
        {/* overscroll-contain stops the scroll handing off to the calendar
            behind once the list hits either end. */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <SlotList cell={cell} clubs={clubs} duration={duration} />
          <div className="pb-[env(safe-area-inset-bottom)]" />
        </div>
      </div>
    </div>
  )
}
